<template>
  <el-drawer
    :visible.sync="drawerVisible"
    custom-class="card-stat-drawer"
    :with-header="false"
    size="50%"
    direction="rtl"
    append-to-body
    @close="onClose"
  >
    <div class="stat-drawer">
      <div class="stat-drawer-header">
        <div class="stat-title-block">
          <div class="stat-title-label">
            <i class="el-icon-bank-card"></i>
            {{ $t("VirtualCard.StaticFeild.Statistic") }}
          </div>
          <div class="stat-card-info-row">
            <div class="stat-card-number stat-inline-value">
              <span>{{ cardData.card_number || "-" }}</span>
              <i
                v-if="cardData.card_number"
                class="el-icon-document-copy stat-action-icon"
                @click="copyText(cardData.card_number)"
              ></i>
            </div>
            <div class="stat-card-meta">
              <div class="stat-card-meta-item">
                <span>{{ $t("VirtualCard.Expiration") }}</span>
                <strong class="stat-inline-value">
                  <span>{{ cardData.expiration || "-" }}</span>
                </strong>
              </div>
              <div class="stat-card-meta-item">
                <span>{{ $t("VirtualCard.SecurityCode") }}</span>
                <strong class="stat-inline-value">
                  <span>{{ showCvv ? (cardData.cvv || "-") : "***" }}</span>
                  <i
                    class="el-icon-view stat-action-icon"
                    :class="{ 'stat-cvv-hidden': !showCvv }"
                    @click="showCvv = !showCvv"
                  ></i>
                </strong>
              </div>
            </div>
          </div>
          <div class="stat-card-tag">{{ cardData.tag || "-" }}</div>
        </div>
        <div class="stat-header-actions">
          <CBadge :color="statusBadgeColor">{{ cardStatus }}</CBadge>
          <button class="stat-close-btn" @click="drawerVisible = false">
            <i class="el-icon-close"></i>
          </button>
        </div>
      </div>

      <div class="stat-summary-grid">
        <div class="stat-summary-card">
          <div class="stat-summary-label">{{ $t("VirtualCard.ModalFeild.Balance") }}</div>
          <div class="stat-summary-value">${{ formatNum(balance) }}</div>
        </div>
        <div class="stat-summary-card">
          <div class="stat-summary-label">{{ $t("VirtualCard.StaticFeild.AuthorizedAmount") }}</div>
          <div class="stat-summary-value">${{ formatNum(statistic.auth_amount) }}</div>
        </div>
        <div class="stat-summary-card">
          <div class="stat-summary-label">{{ $t("VirtualCard.StaticFeild.DeclineRate") }}</div>
          <div
            class="stat-summary-value"
            :class="statistic.declined_rate > 0.2 ? 'colorh' : ''"
          >
            {{ toPercent(statistic.declined_rate) }}
          </div>
        </div>
        <div class="stat-summary-card">
          <div class="stat-summary-label">
            {{ $t("VirtualCard.StaticFeild.ReversRate") }}
            <el-tooltip placement="top">
              <div slot="content">
                <div>{{ $t('VirtualCard.StaticFeild.ReversRateTip') }}</div>
              </div>
              <i class="el-icon-question stat-help-icon"></i>
            </el-tooltip>
          </div>
          <div class="stat-summary-value">{{ toPercent(statistic.revers_rate) }}</div>
        </div>
        <div class="stat-summary-card">
          <div class="stat-summary-label">
            {{ $t("VirtualCard.StaticFeild.Reconciled") }}
            <el-tooltip placement="top">
              <div slot="content">
                <div>{{ $t('VirtualCard.StaticFeild.ReconciledTip') }}</div>
              </div>
              <i class="el-icon-question stat-help-icon"></i>
            </el-tooltip>
          </div>
          <div class="stat-summary-value">${{ formatNum(statistic.reconcile_amount) }}</div>
        </div>
        <div v-if="cardHolderName" class="stat-summary-card">
          <div class="stat-summary-label">{{ $t("ceNavbar.CardHolder") }}</div>
          <div class="stat-summary-value">{{ cardHolderName }}</div>
        </div>
      </div>

      <div class="stat-extra-list" v-if="address">
        <span>{{ $t("VirtualCard.StaticFeild.OrderAddress") }}</span>
        <strong>{{ address }}</strong>
      </div>

      <div class="stat-section-title">{{ transactionTitle }}</div>
      <div class="stat-filter-row">
        <el-input
          class="stat-merchant-input"
          v-model="localFilters.merchant_name"
          clearable
          prefix-icon="el-icon-search"
          :placeholder="$t('CardDetail.MerchantKeywords')"
          @input="emitFilters"
        />
        <el-date-picker
          class="stat-date-picker"
          v-model="localFilters.afterDate"
          type="date"
          value-format="yyyy-MM-dd"
          format="yyyy-MM-dd"
          :placeholder="$t('CardDetail.AfterDate')"
          prefix-icon="el-icon-time"
          @input="emitFilters"
        />
        <el-date-picker
          class="stat-date-picker"
          v-model="localFilters.beforeDate"
          type="date"
          value-format="yyyy-MM-dd"
          format="yyyy-MM-dd"
          :placeholder="$t('CardDetail.BeforeDate')"
          prefix-icon="el-icon-time"
          @input="emitFilters"
        />
        <el-button class="stat-search-btn" @click="handleSearch">
          <i class="el-icon-search"></i>
        </el-button>
      </div>

      <el-table
        v-loading="transactionLoading"
        :data="visibleTransactions"
        class="stat-transaction-table"
        size="small"
        border
        empty-text="No Data"
      >
        <el-table-column
          prop="merchant_name"
          :label="$t('CardDetail.Table.ConsumerMerchant')"
          min-width="150"
        />
        <el-table-column
          :label="$t('CardDetail.Table.AuthorizedAmount')"
          min-width="130"
        >
          <template slot-scope="scope">
            <span :class="Number(scope.row.auth_amt) > 0 ? 'auth-color' : 'ca'">
              {{ formatTransactionAmount(scope.row.auth_amt, scope.row.auth_currency) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          prop="status"
          :label="$t('CardDetail.Table.Status')"
          min-width="110"
        >
          <template slot-scope="scope">
            <span
              class="drawer-status-pill"
              :style="`background: ${getTransactionStatusBg(scope.row.status)}; color: ${getTransactionStatusColor(scope.row.status)}`"
            >
              {{ scope.row.status }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          prop="auth_time"
          :label="$t('CardDetail.Table.ConsumptionTime')"
          min-width="160"
        />
      </el-table>

      <div class="stat-pagination">
        <el-pagination
          background
          small
          layout="prev, pager, next"
          :total="transactionTotal"
          :page-size="10"
          :current-page="transactionPage"
          @current-change="$emit('page-change', $event)"
        />
      </div>
    </div>
  </el-drawer>
</template>

<script>
export default {
  name: 'CardStatisticDrawer',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    cardData: {
      type: Object,
      default: () => ({})
    },
    statistic: {
      type: Object,
      default: () => ({})
    },
    balance: {
      type: [Number, String],
      default: 0
    },
    cardHolderName: {
      type: String,
      default: ''
    },
    address: {
      type: String,
      default: ''
    },
    transactions: {
      type: Array,
      default: () => []
    },
    transactionLoading: {
      type: Boolean,
      default: false
    },
    transactionTotal: {
      type: Number,
      default: 0
    },
    transactionPage: {
      type: Number,
      default: 1
    },
    filters: {
      type: Object,
      default: () => ({
        merchant_name: '',
        afterDate: '',
        beforeDate: ''
      })
    },
    statusBadgeColor: {
      type: String,
      default: 'primary'
    }
  },

  data() {
    return {
      showCvv: false,
      localFilters: this.createFilters(this.filters)
    }
  },

  computed: {
    drawerVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    },

    cardStatus() {
      return this.cardData.status || this.statistic.card_status || ''
    },

    transactionTitle() {
      return this.$i18n.locale === 'en' ? 'Transaction Records' : '交易記錄'
    },

    visibleTransactions() {
      return this.transactions.slice(0, 10)
    }
  },

  watch: {
    visible() {
      this.showCvv = false
    },

    filters: {
      deep: true,
      handler(value) {
        this.localFilters = this.createFilters(value)
      }
    }
  },

  methods: {
    createFilters(filters = {}) {
      return {
        merchant_name: filters.merchant_name || '',
        afterDate: filters.afterDate || '',
        beforeDate: filters.beforeDate || ''
      }
    },

    emitFilters() {
      this.$emit('update:filters', this.createFilters(this.localFilters))
    },

    handleSearch() {
      const filters = this.createFilters(this.localFilters)
      this.$emit('update:filters', filters)
      this.$emit('search', filters)
    },

    onClose() {
      this.showCvv = false
      this.$emit('close')
    },

    formatNum(value) {
      if (!value && value !== 0) return 0

      const str = value.toString()
      const reg = str.indexOf('.') > -1 ? /(\d)(?=(\d{3})+\.)/g : /(\d)(?=(?:\d{3})+$)/g
      return str.replace(reg, '$1,')
    },

    toPercent(point) {
      if (point == 0) {
        return 0
      }
      return `${Number(point * 100).toFixed(2)}%`
    },

    copyText(data) {
      const input = document.createElement('input')
      input.value = data
      document.body.appendChild(input)
      input.select()
      document.execCommand('Copy')
      document.body.removeChild(input)
      this.$message({
        type: 'success',
        message: 'Copied'
      })
    },

    formatTransactionAmount(amount, currency) {
      const prefix = Number(amount) > 0 ? '+' : ''
      const unit = currency ? ` ${currency}` : ''
      return `${prefix}${this.formatNum(amount)}${unit}`
    },

    getTransactionStatusBg(status) {
      return status === 'Settled' || status === 'Authorized'
        ? '#dcfce7'
        : status === 'Declined' || status === 'Decline' || status === 'Failed'
        ? '#fee2e2'
        : status === 'Pending'
        ? '#dbeafe'
        : '#eef2f7'
    },

    getTransactionStatusColor(status) {
      return status === 'Settled' || status === 'Authorized'
        ? '#16a34a'
        : status === 'Declined' || status === 'Decline' || status === 'Failed'
        ? '#dc2626'
        : status === 'Pending'
        ? '#2563eb'
        : '#475569'
    }
  }
}
</script>

<style>
.card-stat-drawer {
  min-width: 680px;
  max-width: 760px;
}

.card-stat-drawer .el-drawer__body {
  height: 100%;
  overflow: hidden;
}

.card-stat-drawer .stat-drawer {
  height: 100%;
  overflow-y: auto;
  padding: 28px 26px 22px;
  background: #fff;
  color: #0f172a;
}

.card-stat-drawer .stat-drawer-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 22px;
  border-bottom: 1px solid #e5e7eb;
}

.card-stat-drawer .stat-title-block {
  min-width: 0;
  flex: 1;
}

.card-stat-drawer .stat-title-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #00bea4;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 10px;
}

.card-stat-drawer .stat-card-number {
  color: #020617;
  font-family: "Courier New", monospace;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
  word-break: break-all;
}

.card-stat-drawer .stat-card-info-row {
  display: flex;
  align-items: flex-start;
  gap: 22px;
}

.card-stat-drawer .stat-card-info-row .stat-card-number {
  flex: 1;
  min-width: 0;
}

.card-stat-drawer .stat-card-meta {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  padding-top: 2px;
}

.card-stat-drawer .stat-card-meta-item {
  min-width: 78px;
}

.card-stat-drawer .stat-card-meta-item span {
  display: block;
  color: #94a3b8;
  font-size: 12px;
  margin-bottom: 4px;
}

.card-stat-drawer .stat-card-meta-item strong {
  color: #020617;
  font-family: "Courier New", monospace;
  font-size: 16px;
  font-weight: 700;
}

.card-stat-drawer .stat-card-meta-item strong span {
  display: inline;
  color: inherit;
  font-size: inherit;
  margin-bottom: 0;
}

.card-stat-drawer .stat-card-tag {
  color: #020617;
  font-size: 18px;
  font-weight: 700;
  margin-top: 8px;
}

.card-stat-drawer .stat-header-actions {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.card-stat-drawer .stat-close-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 50%;
  background: #fff;
  color: #64748b;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
}

.card-stat-drawer .stat-close-btn:hover {
  color: #0f172a;
  border-color: #cbd5e1;
}

.card-stat-drawer .stat-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 24px 0 18px;
}

.card-stat-drawer .stat-summary-card {
  min-height: 78px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.card-stat-drawer .stat-summary-label {
  color: #94a3b8;
  font-size: 12px;
  margin-bottom: 8px;
}

.card-stat-drawer .stat-summary-value {
  color: #020617;
  font-family: "Courier New", monospace;
  font-size: 18px;
  font-weight: 700;
  word-break: break-word;
}

.card-stat-drawer .colorh,
.card-stat-drawer .ca {
  color: #ff1111;
  font-weight: bold;
}

.card-stat-drawer .auth-color {
  color: #00be78;
  font-weight: bold;
}

.card-stat-drawer .stat-inline-value {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.card-stat-drawer .stat-action-icon {
  color: #00bea4;
  font-size: 15px;
  cursor: pointer;
  position: relative;
  font-family: element-icons !important;
}

.card-stat-drawer .stat-action-icon:hover {
  color: #01af98;
}

.card-stat-drawer .stat-cvv-hidden::after {
  content: "";
  position: absolute;
  left: 1px;
  top: 7px;
  width: 15px;
  height: 1px;
  background: currentColor;
  transform: rotate(-35deg);
  transform-origin: center;
}

.card-stat-drawer .stat-help-icon {
  color: #e6a23c;
  font-size: 15px;
  margin-left: 4px;
  cursor: pointer;
  vertical-align: -1px;
}

.card-stat-drawer .stat-extra-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  margin-bottom: 18px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
}

.card-stat-drawer .stat-extra-list span {
  color: #94a3b8;
  font-size: 12px;
}

.card-stat-drawer .stat-extra-list strong {
  color: #0f172a;
  font-size: 13px;
}

.card-stat-drawer .stat-section-title {
  margin: 6px 0 16px;
  color: #020617;
  font-size: 20px;
  font-weight: 700;
}

.card-stat-drawer .stat-filter-row {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 144px 144px 44px;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.card-stat-drawer .stat-filter-row .stat-merchant-input,
.card-stat-drawer .stat-filter-row .stat-date-picker,
.card-stat-drawer .stat-filter-row .stat-search-btn {
  align-self: center;
}

.card-stat-drawer .stat-filter-row .el-input__inner {
  height: 40px;
  line-height: 40px;
  border-radius: 10px;
  border-color: #e5e7eb;
}

.card-stat-drawer .stat-filter-row .stat-date-picker {
  width: 100% !important;
  height: 35px;
  border-radius: 10px;
  border-color: #e5e7eb;
}

.card-stat-drawer .stat-filter-row .stat-date-picker.el-input {
  display: block;
}

.card-stat-drawer .stat-search-btn {
  width: 44px;
  height: 35px;
  padding: 0 !important;
  color: #fff !important;
  background: #00bea4 !important;
  border-color: #00bea4 !important;
  border-radius: 10px !important;
  line-height: 40px !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  vertical-align: top;
  margin: 0 0 3px;
}

.card-stat-drawer .stat-search-btn [class^="el-icon-"] {
  line-height: 1;
}

.card-stat-drawer .stat-transaction-table {
  border-radius: 8px;
  overflow: hidden;
}

.card-stat-drawer .stat-transaction-table .el-table__header-wrapper th {
  background: #f7f7fb;
  color: #0f172a;
  font-weight: 700;
}

.card-stat-drawer .drawer-status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.card-stat-drawer .stat-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

@media screen and (max-width: 1120px) {
  .card-stat-drawer .stat-filter-row {
    grid-template-columns: 1fr 1fr 44px;
  }

  .card-stat-drawer .stat-filter-row .stat-merchant-input {
    grid-column: 1 / 4;
  }

  .card-stat-drawer .stat-filter-row .stat-date-picker {
    width: 100% !important;
  }

  .card-stat-drawer .stat-filter-row .stat-search-btn {
    grid-column: 3;
    grid-row: 2;
  }
}

@media screen and (max-width: 768px) {
  .card-stat-drawer {
    width: 100% !important;
    min-width: 0;
  }

  .card-stat-drawer .stat-drawer {
    padding: 22px 16px;
  }

  .card-stat-drawer .stat-drawer-header,
  .card-stat-drawer .stat-filter-row {
    grid-template-columns: 1fr;
  }

  .card-stat-drawer .stat-card-info-row {
    flex-direction: column;
    gap: 12px;
  }

  .card-stat-drawer .stat-card-meta {
    width: 100%;
  }

  .card-stat-drawer .stat-summary-grid {
    grid-template-columns: 1fr;
  }

  .card-stat-drawer .stat-filter-row {
    display: flex;
    flex-direction: column;
  }

  .card-stat-drawer .stat-search-btn {
    width: 100%;
  }
}
</style>
